import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ScheduleActivity } from '@/types';
import type { ScheduleDateBasisValue, ChartDensity } from '@/components/visuals';
import { TopItemsList, ChartEmptyState, toActivitiesUrl } from '@/components/visuals';
import type { UnitsSpreadBasis } from '@/utils/resolvePlannedUnitsSeries';
import { resolvePlannedUnitsSeries } from '@/utils/resolvePlannedUnitsSeries';
import type { ScheduleAnalyticsShapes } from '@/types/analyticsApi';
import { UnitsPerDayHistogramChart } from './UnitsPerDayHistogramChart';

function peakDateFromSeries(series: { date: string; unitsPerDay: number }[]): string | null {
  if (series.length === 0) return null;
  let best = series[0]!;
  for (const p of series) {
    if (p.unitsPerDay > best.unitsPerDay) best = p;
  }
  return best.date;
}

function fmtUnits(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export interface PlannedUnitsHistogramPanelProps {
  activities: ScheduleActivity[];
  scheduleAnalytics: ScheduleAnalyticsShapes | null;
  unitsSpreadBasis: UnitsSpreadBasis;
  scheduleDateBasis: ScheduleDateBasisValue;
  includeMilestoneUnits: boolean;
  height: number | string;
  showDataLabels: boolean;
  density: ChartDensity;
  /** Focus mode: method breakdown + peak contributors drilldown */
  showDiagnostics: boolean;
}

/** Planned units/day histogram plus methodology counters and peak-day contributor table (focus mode). */
export function PlannedUnitsHistogramPanel({
  activities,
  scheduleAnalytics,
  unitsSpreadBasis,
  scheduleDateBasis,
  includeMilestoneUnits,
  height,
  showDataLabels,
  density,
  showDiagnostics,
}: PlannedUnitsHistogramPanelProps) {
  const built = useMemo(
    () =>
      resolvePlannedUnitsSeries(
        activities,
        { dateBasis: scheduleDateBasis, includeMilestones: includeMilestoneUnits },
        scheduleAnalytics,
        unitsSpreadBasis
      ),
    [activities, scheduleDateBasis, includeMilestoneUnits, scheduleAnalytics, unitsSpreadBasis]
  );

  const { series, stats, totalPlannedUnits, contributorsByDate } = built;
  const peakDate = useMemo(() => peakDateFromSeries(series), [series]);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    setSelectedDate(peakDate);
  }, [peakDate]);

  const sortedContributors = useMemo(() => {
    const date = selectedDate;
    if (!date) return [];
    const rows = [...(contributorsByDate.get(date) ?? [])];
    rows.sort((a, b) => b.dailyContribution - a.dailyContribution);
    return rows;
  }, [contributorsByDate, selectedDate]);

  const peakContributorsSorted = useMemo(() => {
    const date = peakDate;
    if (!date) return [];
    const rows = [...(contributorsByDate.get(date) ?? [])];
    rows.sort((a, b) => b.dailyContribution - a.dailyContribution);
    return rows;
  }, [contributorsByDate, peakDate]);

  const peakValue = peakDate ? (series.find((p) => p.date === peakDate)?.unitsPerDay ?? 0) : 0;

  const dayLoad = selectedDate ? (series.find((p) => p.date === selectedDate)?.unitsPerDay ?? 0) : 0;

  const peakTop = peakContributorsSorted[0];
  const topShareOfPeakDay =
    peakValue > 0 && peakTop ? (peakTop.dailyContribution / peakValue) * 100 : 0;

  if (series.length === 0) {
    return (
      <ChartEmptyState
        message="No time-phased planned units. Add planned units and dates on activities, adjust Dates or Include milestone units if needed."
        actionLabel="View Activities"
        actionHref="/app/activities"
      />
    );
  }

  const cumulativeEnd = series[series.length - 1]?.cumulativeUnits ?? totalPlannedUnits;

  const spreadLabel = stats.usedWorkingDaySpread ? 'Working-day spread (activity calendar)' : 'Calendar-day spread';

  const summaryTopItems = [
    {
      id: 'total',
      label: 'Total planned units (included)',
      value: totalPlannedUnits,
      href: '/app/activities',
    },
    {
      id: 'final',
      label: 'Final cumulative (pairs with Planned Units S-Curve)',
      value: cumulativeEnd,
      href: '/app/activities',
    },
    {
      id: 'peak',
      label: peakDate ? `Peak day load (${peakDate})` : 'Peak day load',
      value: peakValue,
      ...(peakDate
        ? {
            href: '/app/activities' as const,
            onActivate: () => setSelectedDate(peakDate),
          }
        : { href: '/app/activities' as const }),
    },
  ];

  const calcStrip = (
    <p className="text-xs text-muted-foreground leading-relaxed rounded-lg border border-border bg-muted/15 px-4 py-3">
      <span className="text-foreground font-medium">{spreadLabel}</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span>{stats.includedActivityCount.toLocaleString()} rows included</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span>{stats.excludedMilestoneCount.toLocaleString()} milestones excluded</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span>{stats.excludedNoUnitsCount.toLocaleString()} no-unit rows</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span>{stats.excludedInvalidDateCount.toLocaleString()} invalid dates</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span>{(stats.fallbackCalendarCount ?? 0).toLocaleString()} calendar fallbacks</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span>{(stats.unparsedCalendarCount ?? 0).toLocaleString()} unparsed calendars</span>
      <span className="mx-1.5 opacity-50">·</span>
      <span className="text-foreground font-medium tabular-nums">Final total {fmtUnits(totalPlannedUnits)} units</span>
    </p>
  );

  return (
    <div className="flex flex-col gap-4">
      {showDiagnostics ? (
        calcStrip
      ) : (
        <p className="text-xs text-muted-foreground">
          {stats.includedActivityCount} workload rows · {stats.excludedMilestoneCount} milestones excluded ·{' '}
          {stats.usedWorkingDaySpread ? 'Working-day (P6 calendar)' : 'Calendar-day'}
        </p>
      )}

      <UnitsPerDayHistogramChart
        points={series}
        height={height}
        showDataLabels={showDataLabels}
        density={density}
        onBarDateClick={showDiagnostics ? setSelectedDate : undefined}
        highlightedDate={showDiagnostics ? selectedDate : null}
      />

      {showDiagnostics ? (
        <>
          <TopItemsList items={summaryTopItems} valueLabel="Units" valueFormat={(v) => fmtUnits(Number(v))} />

          <div className="border-t border-border pt-4 space-y-3">
            <p className="text-sm font-medium text-foreground">Peak contributors — {selectedDate ?? '—'}</p>
            <p className="text-xs text-muted-foreground">
              Click a histogram bar to inspect that day. Rows are sorted by daily contribution (descending).
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs rounded-lg border border-border bg-muted/15 px-4 py-3">
              <div>
                <span className="text-muted-foreground block">Peak day</span>
                <span className="font-medium tabular-nums text-foreground">{peakDate ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block">Peak units/day</span>
                <span className="font-medium tabular-nums text-foreground">{fmtUnits(peakValue)}</span>
              </div>
              <div>
                <span className="text-muted-foreground block">Top contributor on peak day</span>
                <span className="font-medium text-foreground truncate block" title={peakTop?.activityName}>
                  {peakTop ? `${peakTop.activityId} · ${peakTop.activityName}` : '—'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Top contributor units (peak day)</span>
                <span className="font-medium tabular-nums text-foreground">{peakTop ? fmtUnits(peakTop.dailyContribution) : '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block">Top share of peak day load</span>
                <span className="font-medium tabular-nums text-foreground">
                  {peakTop && peakValue > 0 ? `${topShareOfPeakDay.toFixed(1)}%` : '—'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Selected day total</span>
                <span className="font-medium tabular-nums text-foreground">
                  {selectedDate ? `${selectedDate} · ${fmtUnits(dayLoad)}` : '—'}
                </span>
              </div>
            </div>

            {peakTop && topShareOfPeakDay >= 85 && peakValue > 0 ? (
              <p className="text-xs text-muted-foreground rounded-md border border-border bg-card px-3 py-2">
                Potential concentration issue: one activity contributes {topShareOfPeakDay.toFixed(1)}% of the peak day’s load.
              </p>
            ) : null}

            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="p-2 font-medium text-left">Activity ID</th>
                    <th className="p-2 font-medium text-left">Name</th>
                    <th className="p-2 font-medium text-left">Type</th>
                    <th className="p-2 font-medium text-left">Start</th>
                    <th className="p-2 font-medium text-left">Finish</th>
                    <th className="p-2 font-medium text-right">Total units</th>
                    <th className="p-2 font-medium text-right">Daily</th>
                    <th className="p-2 font-medium text-right">% of peak</th>
                    <th className="p-2 font-medium text-right">Duration (d)</th>
                    <th className="p-2 font-medium text-left">WBS</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContributors.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="p-3 text-muted-foreground">
                        No contributors for this day.
                      </td>
                    </tr>
                  ) : (
                    sortedContributors.map((r, i) => {
                      const pctPeak = peakValue > 0 ? (r.dailyContribution / peakValue) * 100 : 0;
                      return (
                        <tr key={`${r.activityId}-${i}`} className="border-t border-border">
                          <td className="p-2 font-mono tabular-nums align-top">
                            <Link
                              to={toActivitiesUrl({ activity_id: r.activityId })}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {r.activityId}
                            </Link>
                          </td>
                          <td className="p-2 max-w-[200px] break-words align-top">{r.activityName}</td>
                          <td className="p-2 whitespace-nowrap align-top">{r.activityType}</td>
                          <td className="p-2 whitespace-nowrap tabular-nums align-top">{r.startDateUsed}</td>
                          <td className="p-2 whitespace-nowrap tabular-nums align-top">{r.finishDateUsed}</td>
                          <td className="p-2 text-right tabular-nums align-top">{fmtUnits(r.totalPlannedUnits)}</td>
                          <td className="p-2 text-right tabular-nums font-medium align-top">{fmtUnits(r.dailyContribution)}</td>
                          <td className="p-2 text-right tabular-nums align-top">{pctPeak.toFixed(1)}%</td>
                          <td className="p-2 text-right tabular-nums align-top">{r.durationDays}</td>
                          <td className="p-2 max-w-[140px] break-words align-top">{r.wbs}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
